import { Chart, CategoryScale, registerables, ChartEvent, ActiveElement, ChartData } from "chart.js";
import { Line } from "react-chartjs-2";
import dragDataPlugin from "chartjs-plugin-dragdata";
import { ReactElement, useRef, useState, useEffect, useMemo } from "react";
import { MONTHS_LONG } from "../../../../constants/Months";
import { COLORS } from "../../../../constants/Colors";
import { Button } from "@zendeskgarden/react-buttons";
import { DragDataEvent } from "chartjs-plugin-dragdata/dist/types/EventTypes";
import cn from "classnames";
import css from "./lease-expiration-curve.module.scss";
import { ReactComponent as CurveBell } from "../../../../assets/icons/curve_bell.svg";
import { ReactComponent as CurveFlat } from "../../../../assets/icons/curve_flat.svg";
import { ReactComponent as CurveSummerBell } from "../../../../assets/icons/curve_summer_bell.svg";
import { Input } from "@zendeskgarden/react-forms";
import { calculateBellCurve } from "../../BulkAssumptionMathHelpers";
import { Property } from "../../../../contexts/properties/logic";
import { getMonths } from "../../../../utils/months-helper";
import {VersionType} from "../../../../__generated__/generated_types";

Chart.register(CategoryScale, dragDataPlugin, ...registerables);

export interface ILeaseExpirationCurveChartProps {
    currentProperty: Property;
    onValueChange?: (data: number[]) => void;
    existingValues?: number[] | null;
    versionType: VersionType;
}

const NORMAL_POINT_COLOR = COLORS.PRIMARY_200;
const NORMAL_LINE_COLOR = COLORS.PRIMARY_150;
const EMPHASIS_COLOR = COLORS.PRIMARY_500;
const GREY_COLOR = COLORS.GREY_100;

// this is the curve Briant provided as an ideal bell curve, with a peak in the summer
const DEFAULT_SUMMER_CURVE: Map<string, number> = new Map([
    ["Jan", 4],
    ["Feb", 4.8],
    ["Mar", 7.8],
    ["Apr", 9.7],
    ["May", 11.1],
    ["Jun", 12.6],
    ["Jul", 12.6],
    ["Aug", 11.1],
    ["Sep", 9.7],
    ["Oct", 7.8],
    ["Nov", 4.8],
    ["Dec", 4],
]);

export default function LeaseExpirationCurveChart({
    currentProperty,
    onValueChange,
    existingValues,
    versionType,
}: ILeaseExpirationCurveChartProps): ReactElement {
    const changedValueIndex = useRef<number>(0); // tracks the currently active month index
    const yScaleMax = useRef<number>(
        existingValues ? Math.max(...existingValues) + 8 : 16
    ); // dynamic as user drags a point
    const chartInstance = useRef<Chart<"line", number[], string>>();

    const [expirationCurve, setExpirationCurve] = useState<number[]>(
        existingValues ? existingValues : new Array(12).fill(8.3)
    );
    const [menuVisible, setMenuVisible] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const menuRef = useRef<HTMLDivElement>(null);
    const months = getMonths(currentProperty);
    const translatedSummerCurve = months.map(month => DEFAULT_SUMMER_CURVE.get(month) ?? 0);

    const chartData = useMemo<ChartData<"line", number[], string>>(
        () => ({
            labels: months,
            datasets: [
                {
                    data: expirationCurve,
                    pointBackgroundColor: getPointColorArray(),
                    segment: {
                        borderWidth: 3,
                        borderColor: (ctx) => isMonthDisabled(ctx.p0DataIndex) ? GREY_COLOR : NORMAL_LINE_COLOR,
                    },
                },
            ],
        }),
        [expirationCurve]
    );

    function isMonthDisabled(monthIndex: number) {
        return versionType == VersionType.Reforecast && monthIndex < currentProperty.reforecastStartMonthIndex;
    }

    function getPointColorArray(): string[] {
        return new Array(12).fill(null).map((_x, i) => isMonthDisabled(i) ? GREY_COLOR : NORMAL_POINT_COLOR);
    }

    useEffect(() => {
        function handleClickOutside(evt: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(evt.target as Node)) {
                setMenuVisible(false);
                if (chartInstance.current) {
                    const dataset = chartInstance.current.data.datasets[0];
                    if (dataset) {
                        dataset.pointBackgroundColor = getPointColorArray();
                        chartInstance.current.update();
                    }
                }
            }
        }

        if (menuVisible) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuVisible]);

    function updateChartRender() {
        const dataset = chartInstance.current?.data.datasets[0];
        if (!dataset) {
            return;
        }

        const highestValue = Math.max(...dataset.data);
        const newMax = Math.floor(highestValue + 8);
        const clampedMax = Math.min(Math.max(newMax, 0), 100);
        yScaleMax.current = clampedMax;

        if (
            chartInstance.current &&
            chartInstance.current.options &&
            chartInstance.current.options.scales &&
            chartInstance.current.options.scales["y"]
        ) {
            chartInstance.current.options.scales["y"].max = clampedMax;
        }
        chartInstance.current?.update();
    }

    // helper to update the active point color.
    function setActivePoint(index: number) {
        const dataset = chartInstance.current?.data.datasets[0];
        if (!dataset) {
            return;
        }

        const updatedPointColors = getPointColorArray();
        updatedPointColors[index] = EMPHASIS_COLOR;
        dataset.pointBackgroundColor = updatedPointColors;
        chartInstance.current?.update();
    }

    function handleDragStart(index: number) {
        if (chartInstance.current) {
            chartInstance.current.options.animation = false;
        }
        setActivePoint(index);
        setMenuVisible(false);
    }

    function handleDrag() {
        const dataset = chartInstance.current?.data.datasets[0];
        if (!dataset) {
            return;
        }

        updateChartRender();

        if (onValueChange) {
            onValueChange([...dataset.data] as number[]);
        }
    }

    function handleDragEnd(evt: DragDataEvent, index: number) {
        changedValueIndex.current = index;
        const dataset = chartInstance.current?.data.datasets[0];
        if (!dataset) {
            return;
        }

        if (chartInstance.current) {
            delete chartInstance.current.options.animation;
        }

        setExpirationCurve([...dataset.data] as number[]);
        if (onValueChange) {
            onValueChange([...dataset.data] as number[]);
        }

        updateChartRender();

        // reapply the active point color after a slight delay.
        setTimeout(() => {
            setActivePoint(index);
        }, 150);

        // position the menu based on the dragged/clicked point
        if (chartInstance.current) {
            const element = chartInstance.current.getElementsAtEventForMode(
                evt,
                "nearest",
                { intersect: false },
                true
            );
            const pointElement = element[0]?.element;
            if (pointElement && versionType == VersionType.Budget) {
                let xPosition = pointElement.x + 10;
                let yPosition = pointElement.y;
                if (menuRef.current) {
                    if (changedValueIndex.current >= 6) {
                        xPosition -= menuRef.current.scrollWidth + 20;
                    }
                }
                setMenuPosition({
                    x: xPosition,
                    y: yPosition,
                });
                setMenuVisible(true);
            }
        }
    }

    function handleChartOnClick(evt: ChartEvent, elements: ActiveElement[]) {
        const pointElement = elements[0];
        if (pointElement && versionType == VersionType.Budget) {
            const index = pointElement.index;
            changedValueIndex.current = index;
            setActivePoint(index);
            setMenuVisible(true);
        } else if (
            menuVisible &&
            evt.native &&
            menuRef.current &&
            !menuRef.current.contains(evt.native.target as Node)
        ) {
            setMenuVisible(false);
            if (chartInstance.current) {
                const dataset = chartInstance.current.data.datasets[0];
                if (dataset) {
                    dataset.pointBackgroundColor = getPointColorArray();
                    chartInstance.current.update();
                }
            }
        }
    }

    // sets the values for the chart curve and then updates rendering and onChange callback
    function updateChartValues(values: number[]) {
        const dataset = chartInstance.current?.data.datasets[0];
        if (!dataset) {
            return;
        }

        dataset.data = [...values];
        updateChartRender();
        setExpirationCurve([...values]);

        if (onValueChange) {
            onValueChange([...values]);
        }
    }

    // evenly distributes all points (except the last adjusted) so that total equals 100
    function redistributeEvenly() {
        const dataset = chartInstance.current?.data.datasets[0];
        if (!dataset) {
            return;
        }

        const anchorValue = dataset.data[changedValueIndex.current];
        if (typeof anchorValue !== "number") {
            return;
        }

        const totalAvailableForOtherMonths = 100 - anchorValue;
        const distributionAmount = totalAvailableForOtherMonths / 11;

        const newCurveValues = [];
        for (let i = 0; i < dataset.data.length; i++) {
            const val =
                i === changedValueIndex.current ? anchorValue : distributionAmount;
            newCurveValues.push(parseFloat(val.toFixed(1)));
        }

        updateChartValues(newCurveValues);
    }

    // scales all points proportionally so that their total equals 100
    function normalizeToHundred() {
        const dataset = chartInstance.current?.data.datasets[0];
        if (!dataset) {
            return;
        }

        const total = dataset.data.reduce((acc, num) => acc + num, 0);
        const ratio = 100 / total;

        dataset.data = dataset.data.map((num) => {
            if (typeof num === "number") {
                return parseFloat((num * ratio).toFixed(1));
            }
            return 0;
        });

        updateChartValues(dataset.data);
    }

    function setBellCurve() {
        const dataset = chartInstance.current?.data.datasets[0];
        if (!dataset) {
            return;
        }

        const anchorValue = dataset.data[changedValueIndex.current];
        if (typeof anchorValue !== "number") {
            return;
        }

        const newValues = calculateBellCurve(anchorValue / 100, changedValueIndex.current);
        const adjustedNewValues = newValues.map(x => parseFloat((x * 100).toFixed(1)));

        updateChartValues(adjustedNewValues);
    }

    function handleInputChange(
        index: number,
        evt: React.ChangeEvent<HTMLInputElement>
    ) {
        const newValue = parseFloat(evt.target.value);
        if (isNaN(newValue)) {
            return;
        }
        const newCurve = [...expirationCurve];
        newCurve[index] = newValue;
        setExpirationCurve(newCurve);

        if (chartInstance.current?.data.datasets[0]) {
            chartInstance.current.data.datasets[0].data = newCurve;
            chartInstance.current.update();
            updateChartRender(); // recalc yMax so the updated point stays visible
        }
        if (onValueChange) {
            onValueChange(newCurve);
        }
    }

    return (
        <div className={css.curveWrapper}>
            <div className={css.curveChartWrapper}>
                <Line
                    ref={chartInstance}
                    data={chartData}
                    options={{
                        elements: {
                            point: {
                                radius: 10,
                                hoverRadius: 10,
                                borderWidth: 3,
                                borderColor: "#ffffff",
                            },
                        },
                        responsive: true,
                        maintainAspectRatio: false,
                        onClick: (evt, elements) => {
                            handleChartOnClick(evt, elements);
                        },
                        plugins: {
                            tooltip: {
                                displayColors: false,
                                callbacks: {
                                    title: () => "",
                                    label: (tooltipItem) => `${tooltipItem.raw}%`,
                                },
                            },
                            legend: {
                                display: false,
                            },
                            dragData: {
                                round: 1,
                                onDragStart: (_e, _datasetIndex, index, _value) => {
                                    // returning false in this hook will disable the drag event
                                    if (isMonthDisabled(index)) {
                                        return false;
                                    }
                                    handleDragStart(index);
                                },
                                onDrag: handleDrag,
                                onDragEnd: (evt, _datasetIndex, index, _value) => {
                                    handleDragEnd(evt, index);
                                },
                            },
                        },
                        scales: {
                            y: {
                                afterFit(scaleInstance) {
                                    scaleInstance.width = 50;
                                },
                                min: 0,
                                max: yScaleMax.current,
                                grid: {
                                    borderColor: GREY_COLOR,
                                },
                                ticks: {
                                    callback(tickValue) {
                                        return `${tickValue}%`;
                                    },
                                },
                            },
                            x: {
                                grid: {
                                    borderColor: GREY_COLOR,
                                },
                                ticks: {
                                    display: false,
                                },
                            },
                        },
                    }}
                />

                <div
                    ref={menuRef}
                    className={cn(css.curvePopupMenu, menuVisible && css.isVisible)}
                    style={{
                        top: menuPosition.y,
                        left: menuPosition.x,
                    }}
                >
                    <h5 className={css.title}>
                        Auto-adjust other months?
                        <span>{MONTHS_LONG[changedValueIndex.current]}</span>
                    </h5>
                    <Button className={css.curveDistButton} onClick={redistributeEvenly}>
                        Distribute Evenly <CurveFlat className={css.curveIcon} />
                    </Button>
                    <Button className={css.curveDistButton} onClick={setBellCurve}>
                        Create Bell Curve <CurveBell className={css.curveIcon} />
                    </Button>
                </div>
            </div>

            <div className={css.curveCellWrapper}>
                {months.map((month, i) => (
                    <div key={i} className={css.curveCell}>
                        <div>{month}</div>
                        <Input
                            type="number"
                            value={expirationCurve[i]}
                            onChange={evt => handleInputChange(i, evt)}
                            disabled={isMonthDisabled(i)}
                        />
                    </div>
                ))}
            </div>

                <div className={css.footer}>
                    <h5>Current total:
                        <span className={cn(css.total, (expirationCurve.sum() < 98 || expirationCurve.sum() > 102) && css.inError)}>
                            {Math.round(expirationCurve.sum())}%
                        </span>
                    </h5>
                    { versionType == VersionType.Budget &&
                        <>
                            <Button onClick={normalizeToHundred} disabled={Math.round(expirationCurve.sum()) == 100}>
                                Auto-Adjust to Total 100%
                            </Button>
                            <Button className={css.curveDistButton} onClick={() => updateChartValues(translatedSummerCurve)}>
                                Use Summer Bell Curve <CurveSummerBell className={css.curveIcon} />
                            </Button>
                        </>
                    }
                </div>
        </div>
    );
}
